import { SalesTaxFilingFrequencyEnum } from '@commenda/prisma';
import { NonTimezoneSensitiveDate } from '@commenda/utils';

import {
  FILING_FREQUENCY_CONFIG,
  MONTH_NAMES,
  MONTH_NAMES_SHORT,
  VALIDATION_MESSAGES,
} from '../constants';
import {
  CalendarPreviewValidationError,
  FilingCalendarPreviewData,
  FilingPreviewEntry,
  MonthData,
  MonthYearValue,
  PositionedFiling,
} from '../types';

export class CalendarPreviewHelper {
  /** Converts MonthYearValue to ISO date string (first day of month). */
  static monthYearToISOString(value: MonthYearValue): string {
    const date = new NonTimezoneSensitiveDate(value.year, value.month, 1);
    return date.toISO8601String();
  }

  /** Converts MonthYearValue to ISO date string (last day of month). */
  static monthYearToEndOfMonthISOString(value: MonthYearValue): string {
    const date = new NonTimezoneSensitiveDate(value.year, value.month, 1);
    return date.endOf('month').toISO8601String();
  }

  /** Parses ISO date string to MonthYearValue. */
  static isoStringToMonthYear(isoString: string): MonthYearValue {
    const [yearStr, monthStr] = isoString.split('-');
    return {
      month: parseInt(monthStr, 10),
      year: parseInt(yearStr, 10),
    };
  }

  /** Formats date for display (e.g., "Jan 31, 2025"). */
  static formatDateForDisplay(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = NonTimezoneSensitiveDate.fromISO8601String(dateString);
    return `${MONTH_NAMES_SHORT[date.getMonth() - 1]} ${date.getDate()}, ${date.getYear()}`;
  }

  /** Formats period range for display (e.g., "Oct 1, 2025 - Dec 31, 2025"). */
  static formatPeriodRange(startDate: string, endDate: string): string {
    return `${CalendarPreviewHelper.formatDateForDisplay(startDate)} - ${CalendarPreviewHelper.formatDateForDisplay(endDate)}`;
  }

  /** Calculates the number of months between two MonthYearValues. */
  static getMonthsDifference(start: MonthYearValue, end: MonthYearValue): number {
    return (end.year - start.year) * 12 + (end.month - start.month) + 1;
  }

  /** Validates the form inputs for calendar generation. */
  static validateCalendarForm(
    state: string,
    taxPeriodStart: MonthYearValue | null,
    taxPeriodEnd: MonthYearValue | null,
    filingFrequency: SalesTaxFilingFrequencyEnum | null
  ): CalendarPreviewValidationError[] {
    const errors: CalendarPreviewValidationError[] = [];

    if (!state) {
      errors.push({ field: 'state', message: VALIDATION_MESSAGES.STATE_REQUIRED });
    }

    if (!taxPeriodStart) {
      errors.push({
        field: 'taxPeriodStart',
        message: VALIDATION_MESSAGES.TAX_PERIOD_START_REQUIRED,
      });
    }

    if (!filingFrequency) {
      errors.push({
        field: 'filingFrequency',
        message: VALIDATION_MESSAGES.FILING_FREQUENCY_REQUIRED,
      });
    }

    // Validate date range against frequency
    if (taxPeriodStart && taxPeriodEnd && filingFrequency) {
      // Check end is after start using numeric comparison
      const startValue = taxPeriodStart.year * 12 + taxPeriodStart.month;
      const endValue = taxPeriodEnd.year * 12 + taxPeriodEnd.month;

      if (endValue < startValue) {
        errors.push({
          field: 'taxPeriodEnd',
          message: VALIDATION_MESSAGES.END_BEFORE_START,
        });
      } else {
        const frequencyConfig = FILING_FREQUENCY_CONFIG.find(
          (f) => f.value === filingFrequency
        );
        if (frequencyConfig) {
          const monthsDiff = CalendarPreviewHelper.getMonthsDifference(
            taxPeriodStart,
            taxPeriodEnd
          );
          if (monthsDiff < frequencyConfig.minMonths) {
            errors.push({
              field: 'general',
              message: VALIDATION_MESSAGES.DATE_RANGE_TOO_SHORT(
                frequencyConfig.label,
                frequencyConfig.minMonths
              ),
            });
          }
        }
      }
    }

    return errors;
  }

  /** Generates month data for the calendar view. */
  static generateMonthsForYear(year: number): MonthData[] {
    return Array.from({ length: 12 }, (_, index) => ({
      index,
      name: MONTH_NAMES_SHORT[index],
      fullName: MONTH_NAMES[index],
      year,
    }));
  }

  /** Groups filings by month for calendar display. */
  static groupFilingsByMonth(
    filings: FilingPreviewEntry[]
  ): Map<string, FilingPreviewEntry[]> {
    const grouped = new Map<string, FilingPreviewEntry[]>();

    filings.forEach((filing) => {
      const startDate = NonTimezoneSensitiveDate.fromISO8601String(
        filing.reporting_period_start_date
      );
      const key = `${startDate.getYear()}-${startDate.getMonth()}`;

      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(filing);
    });

    return grouped;
  }

  /** Checks if a filing spans multiple months. */
  static filingSpansMultipleMonths(filing: FilingPreviewEntry): boolean {
    const start = NonTimezoneSensitiveDate.fromISO8601String(
      filing.reporting_period_start_date
    );
    const end = NonTimezoneSensitiveDate.fromISO8601String(
      filing.reporting_period_end_date
    );

    return (
      start.getYear() !== end.getYear() || start.getMonth() !== end.getMonth()
    );
  }

  /** Calculates how many month rows a filing should span. */
  static getFilingRowSpan(filing: FilingPreviewEntry): number {
    const start = CalendarPreviewHelper.isoStringToMonthYear(
      filing.reporting_period_start_date
    );
    const end = CalendarPreviewHelper.isoStringToMonthYear(
      filing.reporting_period_end_date
    );
    return CalendarPreviewHelper.getMonthsDifference(start, end);
  }

  /** Converts MonthYearValue to YYYY-MM format string. */
  static monthYearToYYYYMM(value: MonthYearValue): string {
    const month = value.month.toString().padStart(2, '0');
    return `${value.year}-${month}`;
  }

  /** Extracts unique years from filings and returns them sorted. */
  static extractYearsFromFilings(filings: FilingPreviewEntry[]): number[] {
    const years = new Set<number>();

    filings.forEach((filing) => {
      const startDate = NonTimezoneSensitiveDate.fromISO8601String(
        filing.reporting_period_start_date
      );
      const endDate = NonTimezoneSensitiveDate.fromISO8601String(
        filing.reporting_period_end_date
      );
      years.add(startDate.getYear());
      years.add(endDate.getYear());
    });

    return Array.from(years).sort((a, b) => a - b);
  }

  /** Generates month data for multiple years. */
  static generateMonthsForYears(years: number[]): MonthData[] {
    const months: MonthData[] = [];

    years.forEach((year) => {
      for (let index = 0; index < 12; index += 1) {
        months.push({
          index,
          name: MONTH_NAMES_SHORT[index],
          fullName: MONTH_NAMES[index],
          year,
        });
      }
    });

    return months;
  }

  /** Gets the year range from preview data (proposed + existing filings). */
  static getYearRangeFromPreviewData(
    previewData: FilingCalendarPreviewData
  ): number[] {
    const allFilings = [
      ...previewData.proposed_filings,
      ...previewData.existing_filings,
    ];

    if (allFilings.length === 0) {
      return [new Date().getFullYear()];
    }

    return CalendarPreviewHelper.extractYearsFromFilings(allFilings);
  }

  /**
   * Calculates the global month index (0-based) for a given year/month
   * combination relative to a list of months.
   */
  static getGlobalMonthIndex(
    months: MonthData[],
    year: number,
    month: number // 1-12
  ): number {
    return months.findIndex((m) => m.year === year && m.index === month - 1);
  }

  /** Positions filings with their row spans for grid rendering. */
  static positionFilingsForGrid(
    filings: FilingPreviewEntry[],
    months: MonthData[]
  ): PositionedFiling[] {
    return filings.map((filing) => {
      const start = CalendarPreviewHelper.isoStringToMonthYear(
        filing.reporting_period_start_date
      );
      const end = CalendarPreviewHelper.isoStringToMonthYear(
        filing.reporting_period_end_date
      );

      const startMonthIndex = CalendarPreviewHelper.getGlobalMonthIndex(
        months,
        start.year,
        start.month
      );
      const endMonthIndex = CalendarPreviewHelper.getGlobalMonthIndex(
        months,
        end.year,
        end.month
      );

      const rowSpan =
        endMonthIndex >= 0 && startMonthIndex >= 0
          ? endMonthIndex - startMonthIndex + 1
          : 1;

      return {
        filing,
        startMonthIndex: Math.max(0, startMonthIndex),
        rowSpan: Math.max(1, rowSpan),
        laneIndex: 0,
        clusterLaneCount: 1,
      };
    });
  }

  private static filingsTimeOverlap(
    a: PositionedFiling,
    b: PositionedFiling
  ): boolean {
    const aEnd = a.startMonthIndex + a.rowSpan - 1;
    const bEnd = b.startMonthIndex + b.rowSpan - 1;
    return a.startMonthIndex <= bEnd && aEnd >= b.startMonthIndex;
  }

  /**
   * Assigns each positioned filing a lane index and a cluster lane count so
   * overlapping filings render in equal-width horizontal slices (Google
   * Calendar-style). Sort strategy puts the longest filing (and ties broken
   * by earliest start) on lane 0 — the back/leftmost slot — so the shorter
   * overlapping items tab into the remaining slots to the right.
   *
   * `laneIndex` is greedy: pick the lowest lane with no time-overlap already
   * placed. `clusterLaneCount` is the maximum lane count across each filing's
   * transitive overlap cluster (unioned by DFS). Callers render using
   *   left  = laneIndex       * (columnWidth / clusterLaneCount)
   *   width = 1               * (columnWidth / clusterLaneCount)
   * which produces non-overlapping side-by-side slots with legible titles.
   */
  static assignLanesToPositionedFilings(
    positioned: PositionedFiling[]
  ): PositionedFiling[] {
    const sorted = [...positioned].sort((a, b) => {
      if (b.rowSpan !== a.rowSpan) return b.rowSpan - a.rowSpan;
      return a.startMonthIndex - b.startMonthIndex;
    });

    const MAX_LANES = 32;
    const withLanes: PositionedFiling[] = [];
    sorted.forEach((pf) => {
      let lane = 0;
      for (; lane < MAX_LANES; lane += 1) {
        const hasOverlap = withLanes.some(
          (other) =>
            other.laneIndex === lane &&
            CalendarPreviewHelper.filingsTimeOverlap(other, pf)
        );
        if (!hasOverlap) break;
      }
      withLanes.push({ ...pf, laneIndex: lane });
    });

    // Build transitive overlap clusters via DFS so every filing in a connected
    // cluster shares the same clusterLaneCount — rendering stays aligned even
    // across filings that don't directly overlap (A ↔ B ↔ C chain).
    const visited = new Set<number>();
    const result: PositionedFiling[] = new Array(withLanes.length);

    withLanes.forEach((_root, rootIndex) => {
      if (visited.has(rootIndex)) return;
      const clusterIndices: number[] = [];
      const stack: number[] = [rootIndex];
      while (stack.length) {
        const idx = stack.pop()!;
        if (visited.has(idx)) continue;
        visited.add(idx);
        clusterIndices.push(idx);
        const current = withLanes[idx];
        withLanes.forEach((other, otherIdx) => {
          if (visited.has(otherIdx)) return;
          if (CalendarPreviewHelper.filingsTimeOverlap(current, other)) {
            stack.push(otherIdx);
          }
        });
      }

      const clusterLaneCount = clusterIndices.reduce(
        (max, idx) => Math.max(max, withLanes[idx].laneIndex + 1),
        1
      );

      clusterIndices.forEach((idx) => {
        result[idx] = { ...withLanes[idx], clusterLaneCount };
      });
    });

    return result;
  }
}
